import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'react-final-form';

import SectionTitle from '../../components/section-title/SectionTitle';
import MediaSearch from '../../components/media-search/MediaSearch';
import MediaPreviewModal from '../../components/media-preview-modal/MediaPreviewModal';
import MediaSourceSwitch from './components/media-source-switch/MediaSourceSwitch';
import MediaPoolGrid from './components/media-pool-grid/MediaPoolGrid';
import MediaPoolEmptyDisclaimer from './components/media-pool-empty-disclaimer/MediaPoolEmptyDisclaimer';
import CommonLoader from '../../../../components/common-loader/CommonLoader';
import useMediaPoolItems from './hooks/useMediaPoolItems';
import useMediaPoolSearch from './hooks/useMediaPoolSearch';
import { STORY_FORM_FIELDS, STORY_MEDIA_RESTRICTIONS } from '../../constants';
import {
  setMediaPoolSearchQuery,
  setMediaPoolSourceMode,
  setSelectedMedia,
  setSelectedMediaActiveId,
} from '../../../../redux/story/storyReducer';
import { getStoryMediaId, isMediaTypeConflict, normalizePoolSelection } from '../../media/storyMediaItem';
import useStoryMediaTypeConflict from '../../hooks/useStoryMediaTypeConflict';
import MediaTypeConflictModal from '../../components/media-type-conflict-modal/MediaTypeConflictModal';

import scss from './MediaPool.module.scss';

function MediaPool() {
  const { t } = useTranslation('story');
  const dispatch = useDispatch();

  const [previewMedia, setPreviewMedia] = useState(null);

  const searchQuery = useSelector((state) => state.story.mediaPoolSearchQuery);
  const sourceMode = useSelector((state) => state.story.mediaPoolSourceMode);

  const { input: mediaInput } = useField(STORY_FORM_FIELDS.MEDIA);

  const selectedItems = useMemo(() => mediaInput.value ?? [], [mediaInput.value]);
  const selectedIds = useMemo(() => new Set(selectedItems.map(getStoryMediaId)), [selectedItems]);

  useMediaPoolSearch();

  const { items, isEmpty, isLoading } = useMediaPoolItems();
  const { isOpen, incomingType, requestAction, confirm, cancel } = useStoryMediaTypeConflict({ selectedItems });

  const handleSearchChange = (value) => {
    dispatch(setMediaPoolSearchQuery(value));
  };

  const handleSourceModeChange = (mode) => {
    dispatch(setMediaPoolSourceMode(mode));
  };

  const handleSelect = (media) => {
    const normalized = normalizePoolSelection(media);

    if (selectedIds.has(normalized.sourceId)) {
      return;
    }

    requestAction(normalized.type, () => {
      const replace = isMediaTypeConflict(selectedItems, normalized.type);
      const nextItems =
        replace || STORY_MEDIA_RESTRICTIONS.SINGLE_SELECTION_ONLY ? [normalized] : [...selectedItems, normalized];

      mediaInput.onChange(nextItems);
      dispatch(setSelectedMedia(nextItems));
      dispatch(setSelectedMediaActiveId(normalized.sourceId));
    });
  };

  const handlePreview = (media) => {
    setPreviewMedia(media);
  };

  const handleClosePreview = () => {
    setPreviewMedia(null);
  };

  return (
    <div className={scss.wrapper}>
      <SectionTitle title={t('mediaPool')} />

      <div className={scss.content}>
        <MediaSearch value={searchQuery} onChange={handleSearchChange} />
        <MediaSourceSwitch value={sourceMode} onChange={handleSourceModeChange} />

        <div className={scss.results}>
          {isEmpty && !isLoading ? <MediaPoolEmptyDisclaimer sourceMode={sourceMode} /> : null}

          {!isEmpty ? (
            <MediaPoolGrid items={items} selectedIds={selectedIds} onSelect={handleSelect} onPreview={handlePreview} />
          ) : null}

          <CommonLoader isLoading={isLoading} width={40} strokeWidth='3' />
        </div>
      </div>

      <MediaPreviewModal isOpen={previewMedia} onClose={handleClosePreview} media={previewMedia} />

      <MediaTypeConflictModal
        isOpen={isOpen}
        incomingType={incomingType}
        onClose={cancel}
        onCancel={cancel}
        onConfirm={confirm}
      />
    </div>
  );
}

export default MediaPool;
